import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Label,
  useToast,
} from '@ais-ui/ui-core';
import { XIcon } from 'lucide-react';
import {
  Bookmark,
  useDeleteBookmark,
  useSaveSettings,
} from '@ais-ui/websockets';
import React, { useState } from 'react';
import { useSettings } from '../SettingsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { useProfile } from '@ais-ui/profile';

interface BaseMapRemoveServiceProps {
  bookmark: Bookmark;
  className?: string;
}

export const BookmarkRemoveService = ({
  bookmark,
  className,
}: BaseMapRemoveServiceProps) => {
  const { messages } = useLocalization();
  const { mutate: deleteBookmark } = useDeleteBookmark();
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { toast } = useToast();
  const { settings } = useSettings();
  const { mutate: mutateSettings } = useSaveSettings();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { username } = useProfile();
  async function onRemoveService(event: React.FormEvent) {
    event.preventDefault();
    setIsSubmitting(true);

    if (settings?.homeBookmark?.uuid === bookmark.uuid) {
      const updatedSettings = {
        ...settings,
        homeBookmark: undefined,
      };
      mutateSettings(updatedSettings);
    }

    // Save the updated settings
    deleteBookmark(bookmark.uuid, {
      onSuccess: () => {
        toast({
          title: messages.genericToastTitleSuccess,
          description: messages.settingsBookmarksSuccessRemoveMessage,
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
      onError: () => {
        toast({
          title: messages.genericToastTitleError,
          description: messages.settingsBookmarksFailedRemoveMessage,
          variant: 'destructive',
        });
        setIsSubmitting(false);
        setShowDialog(false);
      },
    });
  }

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogTrigger asChild>
        <Button
          id={`delete-bookmark-${bookmark.uuid}`}
          variant="ghost"
          size="icon"
          disabled={bookmark.username !== username}
          className={className}
        >
          <XIcon className="size-4" />
        </Button>
      </DialogTrigger>
      <DialogContent id="delete-bookmarks-service-dialog">
        <DialogHeader className="space-y-4">
          <DialogTitle>{messages.settingsBookmarksDeleteTitle}</DialogTitle>
          <DialogDescription>
            {messages.settingsBaseMapDeleteWarning}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onRemoveService}>
          <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
            <Label className="font-light">
              {messages.settingsBaseMapDeleteNameLabel}
            </Label>
            <Label htmlFor="bookmark-name">{bookmark.name}</Label>
          </div>
          <DialogFooter className="p-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDialog(false)}
              disabled={isSubmitting}
            >
              {messages.genericCancel}
            </Button>
            <Button
              variant="destructive"
              type="submit"
              id="bookmark-service-confirm-delete-button"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? messages.genericDeleting
                : messages.genericDelete}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
